import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import ScrollBasic from '../components/shared/ScrollBasic';
import { ImageScaleView, ImageView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { parseAmount } from 'src/lib/utils';
import platform, { Product } from '@play-co/gcinstant';
import {
  buyProduct,
  buyProductStripe,
  convertPriceToNumber,
} from 'src/lib/iap/iap.util';
import { Iap, ProductId } from 'src/replicant/iap';
import { realMoneyPriceToReadableString } from 'src/lib/iap/iapConversion';
import { ProductReward } from 'src/replicant/iap/iap.ruleset';
import { iapClient } from 'src/lib/iap/iapClient';

type CloseHandler = () => void;

/**
 * Gems shop popup
 */
export default class PopupGemsShop extends PopupBasic {
  private scroll: ScrollBasic<Product>;
  private buttonBack: ButtonScaleViewWithText;
  private close: CloseHandler;
  private closeOnPurchase?: boolean;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close(),
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 820,
      darkerBg: true,
      skipMessage: true,
      tracking: { feature: 'gems-shop', $subFeature: 'gems-shop-popup' },
      titleIcon: {
        image: 'assets/minigame/match3/gem/gameboard_gem_tile.png',
        width: 60,
        height: 60,
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    this.scroll = new ScrollBasic<Product>({
      superview: this.box,
      createItem: (...args) => this.createItem(...args),
      showBg: true,
      firstItemOffset: 62,
      margin: 15,
      rect: {
        x: (this.box.style.width - 512) * 0.5,
        y: 94,
        width: 512,
        height: 586,
      },
    });

    this.buttonBack = new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      superview: this.box,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 50,
      font: bitmapFonts('Body'),
      x: this.box.style.width / 2,
      y: 742,
      width: 228,
      height: 94.7,
      centerOnOrigin: true,
      localeText: () => i18n('basic.close'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return opts.close();
      },
      tracking: { feature: 'gems-shop', $subFeature: 'gems-shop-back' },
    });

    this.createBanner();
  }

  /**
   * On dialog init
   */
  init(args: { titleKey?: string; closeOnPurchase?: boolean }) {
    const titleKey = args?.titleKey || 'currencyExchange.gemsShop.title';
    super.init({
      title: i18n(titleKey),
    });

    this.closeOnPurchase = args.closeOnPurchase ?? false;

    // Swap GCInstant by custom IAP client
    const platform = iapClient;

    this.scroll.setItems(
      platform.catalog
        .filter(({ id }) => Iap.api.isProductId(id))
        .sort((a, b) => {
          return convertPriceToNumber(a.price) - convertPriceToNumber(b.price);
        }),
    );
  }

  /**
   * Create banner
   */
  private createBanner() {
    new ImageView({
      superview: this.box,
      image: 'assets/ui/shop/banners/shop_bpoint.png',
      width: 505,
      height: 63,
      x: this.box.style.width / 2,
      y: 130,
      centerOnOrigin: true,
    });
  }

  /**
   * Create view item
   */
  private createItem(superview: View, _: number, item: Product) {
    const productId = item.id as ProductId;
    const reward = Iap.api.getProductRewards(productId);

    const view = new View({
      tag: 'item',
      superview,
      width: 485,
      height: 109,
    });

    const frame = new ImageScaleView({
      tag: 'frame',
      superview: view,
      image: 'assets/ui/shop/frames/scroll_cell.png',
      scaleMethod: '9slice',
      width: 485,
      height: 109,
      x: (superview.style.width - 485) * 0.5,
      sourceSlices: {
        horizontal: { left: 55, right: 55 },
        vertical: { top: 56.5, bottom: 56.5 },
      },
    });

    const rewardFrame = new ImageScaleView({
      tag: 'rewardFrame',
      superview: view,
      image: 'assets/ui/shop/frames/panel_unit_white.png',
      scaleMethod: '9slice',
      width: 167,
      height: 47,
      x: 45,
      y: 28,
      sourceSlices: {
        horizontal: { left: 22.5, right: 22.5 },
        vertical: { top: 22.5, bottom: 22.5 },
      },
    });

    new ImageView({
      tag: 'gem',
      superview: view,
      image: 'assets/minigame/match3/gem/gameboard_gem_tile.png',
      width: 110,
      height: 91,
      scale: 0.5,
      centerAnchor: true,
      x: 5,
      y: 5,
    });

    new LangBitmapFontTextView({
      tag: 'reward',
      superview: rewardFrame,
      centerAnchor: true,
      align: 'left',
      verticalAlign: 'center',
      color: '#9A7171',
      wordWrap: false,
      x: 43,
      y: 1,
      size: 26,
      width: 118,
      height: 44,
      font: bitmapFonts('Body'),
      localeText: () => `x ${parseAmount(PopupGemsShop.getTotalGems(reward))}`,
      letterSpacing: -2,
    });

    new LangBitmapFontTextView({
      tag: 'bonus',
      superview: view,
      centerAnchor: true,
      align: 'left',
      verticalAlign: 'center',
      color: '#DBA98E',
      wordWrap: false,
      x: 82,
      y: 76,
      size: 15,
      width: 200,
      height: 16,
      font: bitmapFonts('Body'),
      localeText: () => PopupGemsShop.getItemDescription(reward),
      letterSpacing: -2,
    });

    const priceFrame = new ImageScaleView({
      tag: 'priceFrame',
      superview: view,
      image: 'assets/ui/shop/frames/panel_unit.png',
      scaleMethod: '9slice',
      width: 127.19,
      height: 50,
      x: 235,
      y: 27,
      sourceSlices: {
        horizontal: { left: 24, right: 24 },
        vertical: { top: 24, bottom: 24 },
      },
    });

    new LangBitmapFontTextView({
      tag: 'price',
      superview: priceFrame,
      centerAnchor: true,
      align: 'center',
      verticalAlign: 'center',
      color: '#FBFEFA',
      wordWrap: false,
      x: 10,
      y: 8,
      size: 28,
      width: 109,
      height: 36,
      font: bitmapFonts('Body'),
      localeText: () =>
        realMoneyPriceToReadableString(item.price, item.currencyCode),
      letterSpacing: -2,
    });

    view.addSubview(
      new ButtonScaleViewWithText({
        tag: 'exchangeBtn',
        ...uiConfig.buttons.secondary,
        fontSize: 25,
        labelOffsetX: -3,
        font: bitmapFonts('Body'),
        x: 374,
        y: 17,
        width: 112,
        height: 74,
        pressedOffsetY: 2,
        localeText: () => i18n('currencyExchange.gemsShop.exchangeButton'),
        onClick: async () => {
          sounds.sfx.play('ok');

          const purchased = await buyProductStripe({
            productId,
            feature: 'iap',
            subFeature: 'iap-gems-shop',
          });

          if (this.closeOnPurchase && purchased) {
            this.close();
          }
        },
        tracking: { feature: 'gems-shop', $subFeature: 'gems-shop-exchange' },
      }),
    );

    return view;
  }

  private static getItemDescription(reward: ProductReward) {
    const { paidGems, freeGems } = reward;
    if (!freeGems) {
      return '';
    }

    return i18n('currencyExchange.coinsShop.bonus', {
      text: `${parseAmount(paidGems)} + ${parseAmount(freeGems)}`,
    });
  }

  private static getTotalGems(reward: ProductReward) {
    let gems = 0;
    gems += reward.paidGems || 0;
    gems += reward.freeGems || 0;
    return gems;
  }
}
